// The backend's subscription pattern: an exact event, a `family.*` prefix, or `*`.
const SUBSCRIPTION = /^(\*|[a-z_]+\.(\*|[a-z_]+))$/;
const MAX_LENGTH = 60;

export const ALL_EVENTS = '*';

export function familyOf(event: string): string {
  return event.split('.')[0] ?? event;
}

export function familyWildcard(family: string): string {
  return `${family}.*`;
}

/** Same matching the backend uses when it fans an event out to endpoints. */
export function matchesSubscription(subscriptions: readonly string[], event: string): boolean {
  return subscriptions.some(
    (pattern) =>
      pattern === ALL_EVENTS ||
      pattern === event ||
      (pattern.endsWith('.*') && event.startsWith(pattern.slice(0, -1))),
  );
}

export type SubscriptionIssue = 'format' | 'tooLong' | 'unknown';

/**
 * `format` and `tooLong` are refused by the backend. `unknown` is accepted there but can never
 * fire (no known event matches it), which is always a typo, so the form refuses it too.
 */
export function subscriptionIssue(
  pattern: string,
  known: readonly string[],
): SubscriptionIssue | null {
  if (pattern.length > MAX_LENGTH) return 'tooLong';
  if (!SUBSCRIPTION.test(pattern)) return 'format';
  if (known.length > 0 && !known.some((event) => matchesSubscription([pattern], event))) {
    return 'unknown';
  }
  return null;
}

/**
 * Drops duplicates and patterns a broader one already covers: `*` swallows everything and
 * `order.*` swallows `order.paid`. Order of first appearance is kept.
 */
export function normaliseSubscriptions(patterns: readonly string[]): string[] {
  const unique = [...new Set(patterns.map((p) => p.trim()).filter(Boolean))];
  if (unique.includes(ALL_EVENTS)) return [ALL_EVENTS];
  const wildcards = unique.filter((p) => p.endsWith('.*'));
  return unique.filter(
    (p) => p.endsWith('.*') || !wildcards.some((w) => p.startsWith(w.slice(0, -1))),
  );
}

/** Known events grouped by family, in the backend's order. */
export function groupByFamily(events: readonly string[]): Array<[string, string[]]> {
  const groups = new Map<string, string[]>();
  for (const event of events) {
    const family = familyOf(event);
    groups.set(family, [...(groups.get(family) ?? []), event]);
  }
  return [...groups.entries()];
}

/** Subscriptions the checkbox tree cannot show: typed patterns outside the known list. */
export function customSubscriptions(
  patterns: readonly string[],
  known: readonly string[],
): string[] {
  const families = new Set(known.map((event) => familyWildcard(familyOf(event))));
  return patterns.filter((p) => p !== ALL_EVENTS && !known.includes(p) && !families.has(p));
}

/** https anywhere, plain http only for a local receiver (the backend's rule). */
export function isEndpointUrl(value: string): boolean {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  if (url.protocol === 'https:') return true;
  return url.protocol === 'http:' && (url.hostname === 'localhost' || url.hostname === '127.0.0.1');
}
